/**
 * US-504a. The judging draft, as passed between the session that owns it and
 * the surfaces that render it.
 *
 * Props, deliberately, and not a React context. A context would let any future
 * component read judging state — and the blindness rule this epic keeps
 * re-learning is only enforceable while the set of things that can see a value
 * is enumerable. Every consumer of a draft is a call site you can grep for.
 */
import type { Verdict } from '../../constants/calibration-chips';
import type { CalibrationIdentityResponse } from '../../types/calibration-api';

/**
 * The recruiter's in-progress judgment of ONE candidate.
 *
 * Owned by `CalibrationJudgingSession`, which the page mounts with
 * `key={row.id}` — so a different candidate is a different component instance
 * and a different draft, with no effect-driven reset. That remount is
 * load-bearing: before it existed, the previous candidate's verdict and note
 * carried over and were submitted against the next person.
 */
export interface JudgingDraft {
  verdict: Verdict | null;
  selectedChip: string | null;
  note: string;
  /**
   * Set only in edit mode, when the chip already recorded against this
   * judgment has no button to press. Two distinct reasons, and they are not
   * the same claim — see the derivation in `CalibrationJudgingSession`.
   */
  unavailableChip: { label: string; retired: boolean } | null;
}

/** Everything a judging surface can do to the draft. */
export interface JudgingHandlers {
  onVerdictChange: (verdict: Verdict) => void;
  onChipClick: (chipId: string) => void;
  onNoteChange: (note: string) => void;
  onSave: () => void;
}

/**
 * The single `useCalibrationIdentity` instance, shared by the card and the
 * dossier. One instance, not two: the reveal endpoint is set-once server-side,
 * so a second instance would not corrupt anything there — but it would mean a
 * reveal in the dossier left the card still showing the blind state, which is
 * precisely the "two surfaces, two truths" failure this session exists to
 * prevent.
 */
export interface IdentityState {
  identity: CalibrationIdentityResponse | null;
  isRevealing: boolean;
  error: string | null;
  onReveal: () => void;
}

/**
 * US-504b. Positional keyboard letters for the reason chips.
 *
 * `r` is DELIBERATELY SKIPPED. The mockup binds chips across `qwertyu` and
 * checks `r` for "open dossier" first, so a chip landing on `r` is silently
 * unreachable — a dead key nobody would notice until a recruiter complained
 * that one reason could not be typed. Seven letters covers the longest chip
 * set (the negative one, seven chips today).
 *
 * The map is web-local and positional rather than a `key` field on
 * `calibration-chips.ts`: that file's drift test regex-parses `{id, label,
 * polarity}` tuples out of this package's mirror, and a fourth field breaks it
 * for zero product value.
 */
export const CALIBRATION_CHIP_KEYS = ['q', 'w', 'e', 't', 'y', 'u', 'i'] as const;
